import type { FaultlineDefinition, FaultlinesInput } from './types';

const DEFAULT_THRESHOLD = 35;

function isHighChange(input: FaultlinesInput) {
  return input.changeRate === 'high';
}

export const FAULTLINES: FaultlineDefinition[] = [
  {
    id: 'public-edge',
    title: 'Public Edge Exposure',
    baseScore: 6,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'edge-internet', points: 32, when: (i) => i.exposure === 'internet' },
      { id: 'edge-mixed', points: 20, when: (i) => i.exposure === 'mixed' },
      { id: 'edge-roaming', points: 12, when: (i) => i.deployment === 'edge' },
      {
        id: 'edge-mobile',
        points: 8,
        when: (i) => i.systemType === 'mobile' || i.systemType === 'iot',
      },
      { id: 'edge-weak-obs', points: 10, when: (i) => i.observability === 'minimal' },
    ],
    signals: [
      'Public endpoints without rate limits or origin protections.',
      'Unclear asset inventory or stale DNS/edge routes.',
      'Edge traffic lacks anomaly detection.',
    ],
    moves: [
      'Establish a dedicated edge tier with explicit allowlists and rate limits.',
      'Separate public entry points from core services.',
      'Add request logging and anomaly alerts at the edge.',
    ],
    failureModes: ['Edge compromise flows directly into core services.'],
    questions: [
      'Which endpoints are public, and how are they authenticated?',
      'How do you rate limit or block abusive traffic?',
      'What is the blast radius if the edge is compromised?',
    ],
    nextSteps: [
      'Map every public endpoint and add rate limits to the highest-risk ones.',
    ],
  },
  {
    id: 'identity-session',
    title: 'Identity & Session Weakness',
    baseScore: 7,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'id-passwords', points: 22, when: (i) => i.identity === 'passwords' },
      { id: 'id-shared', points: 18, when: (i) => i.identity === 'shared' },
      { id: 'id-mixed', points: 10, when: (i) => i.identity === 'mixed' },
      { id: 'id-internet', points: 10, when: (i) => i.exposure !== 'internal' },
      { id: 'id-multi', points: 10, when: (i) => i.multiTenancy === 'multi' },
      { id: 'id-admin-shared', points: 8, when: (i) => i.privilegedAccess === 'shared' },
    ],
    signals: [
      'Long-lived sessions or tokens without rotation.',
      'Inconsistent MFA or device checks.',
      'Manual user provisioning across services.',
    ],
    moves: [
      'Centralize identity with short-lived tokens.',
      'Enforce device-aware auth on privileged flows.',
      'Implement fast revocation paths for users and devices.',
    ],
    failureModes: ['Stolen credentials persist across multiple services.'],
    questions: [
      'How fast can you revoke a user or device?',
      'Where do sessions live and how long do they last?',
      'Which systems still rely on shared secrets for identity?',
    ],
    nextSteps: ['Shorten session lifetimes for your most sensitive endpoints.'],
  },
  {
    id: 'privileged-access',
    title: 'Privileged Access / Admin Plane',
    baseScore: 7,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'priv-shared', points: 26, when: (i) => i.privilegedAccess === 'shared' },
      { id: 'priv-unknown', points: 16, when: (i) => i.privilegedAccess === 'unknown' },
      { id: 'priv-env', points: 10, when: (i) => i.secretsHandling === 'env' },
      { id: 'priv-min-obs', points: 8, when: (i) => i.observability === 'minimal' },
      { id: 'priv-internet', points: 8, when: (i) => i.exposure !== 'internal' },
      { id: 'priv-change', points: 8, when: isHighChange },
    ],
    signals: [
      'Shared admin credentials or static break-glass accounts.',
      'Admin endpoints reachable from the same network as user traffic.',
      'Missing audit trails for admin actions.',
    ],
    moves: [
      'Separate the admin plane from user-facing traffic.',
      'Adopt per-person admin accounts with just-in-time access.',
      'Log and alert on privileged actions.',
    ],
    failureModes: ['Admin credentials leak gives broad control.'],
    questions: [
      'Is the admin plane reachable from the public path?',
      'How do you rotate and revoke admin credentials?',
      'What is the shortest path to isolate admin access?',
    ],
    nextSteps: ['Isolate admin endpoints behind a dedicated network boundary.'],
  },
  {
    id: 'secrets-sprawl',
    title: 'Secrets Sprawl',
    baseScore: 6,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'secret-env', points: 22, when: (i) => i.secretsHandling === 'env' },
      { id: 'secret-unknown', points: 12, when: (i) => i.secretsHandling === 'unknown' },
      { id: 'secret-shared', points: 10, when: (i) => i.identity === 'shared' },
      { id: 'secret-change', points: 10, when: isHighChange },
      {
        id: 'secret-incidents',
        points: 10,
        when: (i) => i.incidentHistory === 'some' || i.incidentHistory === 'frequent',
      },
      { id: 'secret-min-obs', points: 8, when: (i) => i.observability === 'minimal' },
    ],
    signals: [
      'Secrets embedded in env files or configs.',
      'Manual rotation with inconsistent ownership.',
      'Duplicate credentials shared across services.',
    ],
    moves: [
      'Centralize secrets in a manager with scoped access.',
      'Automate rotation and shorten credential lifetimes.',
      'Split shared secrets into per-service credentials.',
    ],
    failureModes: ['Leaked token grants broad access across services.'],
    questions: [
      'How many services share a credential today?',
      'How often do secrets rotate automatically?',
      'Where are the highest-value secrets stored?',
    ],
    nextSteps: ['Inventory all secrets and move the highest-risk ones into a manager.'],
  },
  {
    id: 'supply-chain',
    title: 'Supply Chain & CI/CD',
    baseScore: 5,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'sc-change', points: 22, when: isHighChange },
      { id: 'sc-cloud', points: 10, when: (i) => i.deployment !== 'on-prem' },
      { id: 'sc-data', points: 10, when: (i) => i.dataSensitivity === 'high' },
      { id: 'sc-multi', points: 8, when: (i) => i.multiTenancy === 'multi' },
      {
        id: 'sc-keywords',
        points: 8,
        when: (_i, k) => k.hits['supply-chain'].length > 0,
      },
    ],
    signals: [
      'Unsigned or unverifiable build artifacts.',
      'CI tokens with broad permissions.',
      'Dependencies updated without provenance checks.',
    ],
    moves: [
      'Adopt signed builds and artifact provenance.',
      'Lock dependency versions and monitor for updates.',
      'Restrict CI credentials to the minimum required.',
    ],
    failureModes: ['Compromised build pipeline injects malicious code.'],
    questions: [
      'Are build artifacts signed and verified?',
      'What can CI tokens access in production?',
      'How do you handle dependency updates safely?',
    ],
    nextSteps: ['Start signing build artifacts for your most critical services.'],
  },
  {
    id: 'multi-tenant',
    title: 'Multi-tenant Isolation',
    baseScore: 4,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'mt-multi', points: 30, when: (i) => i.multiTenancy === 'multi' },
      { id: 'mt-data', points: 12, when: (i) => i.dataSensitivity === 'high' },
      { id: 'mt-internet', points: 10, when: (i) => i.exposure !== 'internal' },
      { id: 'mt-saas', points: 10, when: (i) => i.systemType === 'saas' },
      {
        id: 'mt-keywords',
        points: 6,
        when: (_i, k) => k.hits['multi-tenant'].length > 0,
      },
    ],
    signals: [
      'Shared databases without strong tenant boundaries.',
      'Admin tools that bypass tenant checks.',
      'Missing tenant-scoped encryption.',
    ],
    moves: [
      'Enforce tenant isolation at the data layer.',
      'Add per-tenant encryption keys.',
      'Test cross-tenant access paths deliberately.',
    ],
    failureModes: ['One tenant can access another tenant’s data.'],
    questions: [
      'How do you prevent cross-tenant reads?',
      'Can admin tools bypass tenant boundaries?',
      'What does a tenant-level breach expose?',
    ],
    nextSteps: ['Add tenant-level access tests to your CI pipeline.'],
  },
  {
    id: 'lateral-movement',
    title: 'Lateral Movement / Network Segmentation',
    baseScore: 5,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'lm-hybrid', points: 15, when: (i) => i.deployment === 'hybrid' },
      { id: 'lm-onprem', points: 10, when: (i) => i.deployment === 'on-prem' },
      { id: 'lm-internal', points: 10, when: (i) => i.exposure === 'internal' },
      { id: 'lm-admin-shared', points: 8, when: (i) => i.privilegedAccess === 'shared' },
      { id: 'lm-min-obs', points: 8, when: (i) => i.observability === 'minimal' },
      {
        id: 'lm-keywords',
        points: 6,
        when: (_i, k) => k.hits['lateral-movement'].length > 0,
      },
    ],
    signals: [
      'Flat networks without explicit service boundaries.',
      'Broad service-to-service access without allowlists.',
      'No segmentation testing or validation.',
    ],
    moves: [
      'Segment networks and service identity.',
      'Deny by default with explicit allowlists.',
      'Limit east-west connectivity to known paths.',
    ],
    failureModes: ['A single compromise spreads laterally to core systems.'],
    questions: [
      'What stops a compromised service from reaching data stores?',
      'How do you validate segmentation rules?',
      'Which services can reach the admin plane?',
    ],
    nextSteps: ['Document east-west flows and restrict the top three.'],
  },
  {
    id: 'data-exfil',
    title: 'Data Exfiltration Paths',
    baseScore: 6,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'de-high', points: 26, when: (i) => i.dataSensitivity === 'high' },
      { id: 'de-internet', points: 10, when: (i) => i.exposure !== 'internal' },
      { id: 'de-min-obs', points: 10, when: (i) => i.observability === 'minimal' },
      {
        id: 'de-regulated',
        points: 6,
        when: (i) =>
          i.industry === 'regulated' ||
          i.industry === 'healthcare' ||
          i.industry === 'finance',
      },
      {
        id: 'de-incidents',
        points: 10,
        when: (i) => i.incidentHistory === 'some' || i.incidentHistory === 'frequent',
      },
      { id: 'de-keywords', points: 6, when: (_i, k) => k.hits['data-exfil'].length > 0 },
    ],
    signals: [
      'Unmonitored data egress paths.',
      'Sensitive data replicated across multiple stores.',
      'Weak visibility into downloads or exports.',
    ],
    moves: [
      'Map data flows and reduce unnecessary copies.',
      'Add egress monitoring and alerts.',
      'Encrypt sensitive data at rest and in transit.',
    ],
    failureModes: ['Quiet data exfiltration via an overlooked path.'],
    questions: [
      'Where does sensitive data leave the system?',
      'How do you detect unusual egress?',
      'Which systems hold the most sensitive data?',
    ],
    nextSteps: ['Document data egress paths and alert on unusual volume.'],
  },
  {
    id: 'change-misconfig',
    title: 'Change-Induced Misconfiguration',
    baseScore: 5,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'cm-high', points: 26, when: isHighChange },
      { id: 'cm-medium', points: 12, when: (i) => i.changeRate === 'medium' },
      { id: 'cm-cloud', points: 10, when: (i) => i.deployment !== 'on-prem' },
      { id: 'cm-min-obs', points: 10, when: (i) => i.observability === 'minimal' },
      { id: 'cm-env', points: 8, when: (i) => i.secretsHandling === 'env' },
      {
        id: 'cm-incidents',
        points: 8,
        when: (i) => i.incidentHistory === 'some' || i.incidentHistory === 'frequent',
      },
    ],
    signals: [
      'Manual configuration changes without validation.',
      'Staging and production drift.',
      'Frequent hotfixes without guardrails.',
    ],
    moves: [
      'Automate deployments with guardrails and approvals.',
      'Add config linting and policy checks.',
      'Use canary releases with fast rollback.',
    ],
    failureModes: ['A small config change exposes a large surface.'],
    questions: [
      'How do you detect drift from baseline?',
      'How fast can you roll back a bad change?',
      'Which configs are most error-prone?',
    ],
    nextSteps: ['Add automated config checks to your deployment pipeline.'],
  },
  {
    id: 'telemetry-blind',
    title: 'Telemetry Blind Spots',
    baseScore: 4,
    threshold: DEFAULT_THRESHOLD,
    rules: [
      { id: 'tb-minimal', points: 30, when: (i) => i.observability === 'minimal' },
      { id: 'tb-basic', points: 15, when: (i) => i.observability === 'basic' },
      { id: 'tb-internet', points: 8, when: (i) => i.exposure !== 'internal' },
      { id: 'tb-change', points: 8, when: isHighChange },
      {
        id: 'tb-incidents',
        points: 8,
        when: (i) => i.incidentHistory === 'frequent',
      },
    ],
    signals: [
      'No centralized logs or traces.',
      'Missing auth and admin event visibility.',
      'No alerting on anomalous access or failures.',
    ],
    moves: [
      'Establish baseline logs, metrics, and traces.',
      'Alert on auth and data access anomalies.',
      'Instrument critical flows end-to-end.',
    ],
    failureModes: ['Incidents go unnoticed until impact is obvious.'],
    questions: [
      'What would tell you a breach started?',
      'Which events are unobservable today?',
      'How quickly can you spot abnormal access?',
    ],
    nextSteps: ['Start with auth and data access logging across critical paths.'],
  },
];
